import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Trash2 } from 'lucide-react';
import api from '../services/api';
import { useI18n } from '../context/useI18n';
import { isFitLiveUiEnabled } from '../utils/fitLiveModule';

const MODULES_DEFAULT = [
  { code: 'food', label: 'FOOD', programs: false },
  { code: 'training', label: 'TRAINING', programs: false },
  { code: 'd28d', label: 'Gimnasio virtual', programs: true },
  { code: 'gym', label: 'GYM', programs: true },
];

const MODULES_FIT_LIVE = [
  { code: 'food', label: 'FOOD', programs: false },
  { code: 'training', label: 'TRAINING', programs: false },
  { code: 'fit-live', label: 'Fit Live', programs: false },
];

function emptyRow(moduleCode, programId = '') {
  return {
    module_code: moduleCode,
    program_id: programId || '',
    label: moduleCode === 'fit-live' ? 'Fit Live' : '',
    // Vacío a propósito: pegar permalink productivo (VPOS_…), nunca test_VPOS_*.
    payment_url: '',
    active: true,
    in_person_enabled: true,
    in_person_label: 'Pago en sede',
    online_label: 'Pago en línea (Wompi)',
    sort_order: 0,
  };
}

function rowKey(moduleCode, programId = '') {
  return `${moduleCode}::${programId || ''}`;
}

function PaymentLinkForm({
  row,
  savingKey,
  onPatch,
  onSave,
  onDelete,
  t,
  title,
  hint,
}) {
  const key = rowKey(row.module_code, row.program_id);
  return (
    <div className="card p-4 space-y-3 ui-light-panel surface-readable">
      {title ? <h4 className="font-semibold text-stone-900">{title}</h4> : null}
      {hint ? <p className="text-sm text-stone-600">{hint}</p> : null}
      <div>
        <label className="label text-sm">{t('payments.wompi_url', 'Enlace Wompi (pago en línea)')}</label>
        <input
          className="input w-full"
          placeholder="https://checkout.wompi.co/l/VPOS_…"
          value={row.payment_url || ''}
          onChange={(e) => onPatch({ payment_url: e.target.value, label: row.label || row.module_code })}
        />
      </div>
      <div>
        <label className="label text-sm">{t('payments.online_label', 'Etiqueta pago en línea')}</label>
        <input
          className="input w-full"
          value={row.online_label || 'Pago en línea (Wompi)'}
          onChange={(e) => onPatch({ online_label: e.target.value })}
        />
      </div>
      <label className="flex items-center gap-2 text-sm text-stone-800">
        <input
          type="checkbox"
          checked={row.in_person_enabled !== false}
          onChange={(e) => onPatch({ in_person_enabled: e.target.checked })}
        />
        {t('payments.in_person_enabled', 'Habilitar pago en sede')}
      </label>
      {row.in_person_enabled !== false && (
        <div>
          <label className="label text-sm">{t('payments.in_person_label', 'Etiqueta pago en sede')}</label>
          <input
            className="input w-full"
            value={row.in_person_label || 'Pago en sede'}
            onChange={(e) => onPatch({ in_person_label: e.target.value })}
          />
        </div>
      )}
      <label className="flex items-center gap-2 text-sm text-stone-800">
        <input
          type="checkbox"
          checked={row.active === true}
          onChange={(e) => onPatch({ active: e.target.checked })}
        />
        {t('payments.active', 'Activo (visible en registro si hay URL)')}
      </label>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="btn-primary"
          disabled={savingKey === key}
          onClick={() => onSave(row)}
        >
          {savingKey === key ? t('appearance.saving', 'Guardando…') : t('payments.save', 'Guardar')}
        </button>
        {row.program_id && onDelete ? (
          <button
            type="button"
            className="btn-secondary"
            disabled={savingKey === key}
            onClick={() => onDelete(row)}
          >
            <Trash2 className="w-4 h-4 inline mr-1" />
            {t('payments.delete_override', 'Quitar (usar módulo)')}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function AdminPaymentLinks({ onBack }) {
  const { t } = useI18n();
  const MODULES = isFitLiveUiEnabled() ? MODULES_FIT_LIVE : MODULES_DEFAULT;
  const [links, setLinks] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState('');
  const [draftProgramByModule, setDraftProgramByModule] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [linksRes, programsRes] = await Promise.all([
        api.get('/payment-links/admin'),
        isFitLiveUiEnabled()
          ? Promise.resolve({ data: { data: [] } })
          : api.get('/programs', { params: { platform_only: 'true' } }).catch(() => ({ data: { data: [] } })),
      ]);
      setLinks(linksRes.data.data || []);
      const progList = (programsRes.data?.data || []).filter((p) => p.active !== false);
      setPrograms(progList);
    } catch {
      setError(t('payments.load_error', 'No se pudieron cargar los enlaces de pago'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const findRow = useCallback((moduleCode, programId = '') => {
    const pid = programId || '';
    return links.find((l) => l.module_code === moduleCode && (l.program_id || '') === pid)
      || emptyRow(moduleCode, pid);
  }, [links]);

  const patchLocal = (moduleCode, programId, patchObj) => {
    const pid = programId || '';
    const key = rowKey(moduleCode, pid);
    setLinks((prev) => {
      const idx = prev.findIndex((x) => rowKey(x.module_code, x.program_id) === key);
      const base = idx >= 0 ? prev[idx] : emptyRow(moduleCode, pid);
      const row = { ...base, module_code: moduleCode, program_id: pid, ...patchObj };
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = row;
        return next;
      }
      return [...prev, row];
    });
  };

  const save = async (row) => {
    const key = rowKey(row.module_code, row.program_id);
    setSaving(key);
    setError('');
    try {
      await api.put('/payment-links/admin', {
        ...row,
        program_id: row.program_id || '',
      });
      await load();
    } catch {
      setError(t('payments.save_error', 'Error al guardar'));
    } finally {
      setSaving('');
    }
  };

  const removeOverride = async (row) => {
    if (!row.program_id) return;
    const key = rowKey(row.module_code, row.program_id);
    setSaving(key);
    setError('');
    try {
      await api.delete('/payment-links/admin', {
        data: { module_code: row.module_code, program_id: row.program_id },
      });
      await load();
    } catch {
      setError(t('payments.delete_error', 'No se pudo eliminar la configuración del programa'));
    } finally {
      setSaving('');
    }
  };

  const activePrograms = useMemo(
    () => programs.filter((p) => p.id && p.active !== false),
    [programs],
  );

  if (loading) return <p className="d28d-text-muted">{t('payments.loading', 'Cargando enlaces…')}</p>;

  return (
    <div className="dashboard-main-view surface-readable space-y-6">
      <div>
        {onBack ? (
          <button type="button" className="btn-secondary text-sm mb-2" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 inline mr-1" />
            Volver a Maestros
          </button>
        ) : null}
        <h2 className="d28d-page-title">{t('payments.title', 'Enlaces y métodos de pago')}</h2>
        <p className="d28d-text-muted">
          {t(
            'payments.subtitle_programs',
            'Por módulo (base) y, si hace falta, por programa activo. Si un programa no tiene configuración propia, usa la del módulo.',
          )}
        </p>
      </div>
      {error && <p className="text-red-600 text-sm">{error}</p>}
      <div className="space-y-8">
        {MODULES.map((m) => {
          const moduleRow = findRow(m.code, '');
          const overrides = links.filter((l) => l.module_code === m.code && (l.program_id || ''));
          const draftPid = draftProgramByModule[m.code] || '';
          const usedProgramIds = new Set(overrides.map((o) => o.program_id));
          const availablePrograms = activePrograms.filter((p) => !usedProgramIds.has(String(p.id)));

          return (
            <section key={m.code} className="space-y-3">
              <h3 className="font-semibold text-lg text-stone-900">{m.label}</h3>
              <PaymentLinkForm
                row={moduleRow}
                savingKey={saving}
                t={t}
                title={t('payments.module_default', 'Configuración base del módulo')}
                hint={t('payments.module_default_hint', 'Se usa cuando el programa no tiene configuración propia.')}
                onPatch={(patchObj) => patchLocal(m.code, '', patchObj)}
                onSave={save}
              />

              {m.programs ? (
                <div className="space-y-3 sm:border-l-2 sm:border-stone-200 sm:pl-4">
                  <h4 className="font-medium text-stone-800">
                    {t('payments.program_overrides', 'Configuración por programa activo')}
                  </h4>
                  {overrides.map((o) => {
                    const prog = activePrograms.find((p) => String(p.id) === String(o.program_id));
                    return (
                      <PaymentLinkForm
                        key={rowKey(o.module_code, o.program_id)}
                        row={findRow(o.module_code, o.program_id)}
                        savingKey={saving}
                        t={t}
                        title={prog?.name || o.program_id}
                        hint={`program_id: ${o.program_id}`}
                        onPatch={(patchObj) => patchLocal(o.module_code, o.program_id, patchObj)}
                        onSave={save}
                        onDelete={removeOverride}
                      />
                    );
                  })}
                  {availablePrograms.length ? (
                    <div className="flex flex-wrap items-end gap-2">
                      <div className="min-w-[220px] flex-1">
                        <label className="label text-sm">{t('payments.add_program', 'Añadir configuración de programa')}</label>
                        <select
                          className="input w-full"
                          value={draftPid}
                          onChange={(e) => setDraftProgramByModule((prev) => ({ ...prev, [m.code]: e.target.value }))}
                        >
                          <option value="">{t('payments.select_program', 'Seleccionar programa…')}</option>
                          {availablePrograms.map((p) => (
                            <option key={p.id} value={p.id}>{p.name || p.id}</option>
                          ))}
                        </select>
                      </div>
                      <button
                        type="button"
                        className="btn-secondary"
                        disabled={!draftPid}
                        onClick={() => {
                          if (!draftPid) return;
                          patchLocal(m.code, draftPid, emptyRow(m.code, draftPid));
                          setDraftProgramByModule((prev) => ({ ...prev, [m.code]: '' }));
                        }}
                      >
                        {t('payments.create_override', 'Crear configuración')}
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm text-stone-600">
                      {t('payments.no_more_programs', 'No hay más programas activos sin configuración propia.')}
                    </p>
                  )}
                </div>
              ) : null}
            </section>
          );
        })}
      </div>
    </div>
  );
}
